import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Check, X } from "lucide-react";

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

/** A pop-up in the middle of the screen. Closes with the X, the Escape key, or a click outside it. */
export function Modal({ title, icon: Icon, onClose, testid, children, wide = false, backdropCloses = true }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") closeRef.current(); };
    document.addEventListener("keydown", key);
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", key); document.body.style.overflow = before; };
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/60 p-4" data-testid={`${testid}-backdrop`} onClick={() => backdropCloses && closeRef.current()}>
      <div role="dialog" aria-modal="true" aria-label={title} data-testid={testid} onClick={(e) => e.stopPropagation()}
        className={`max-h-[88vh] w-full ${wide ? "max-w-xl" : "max-w-md"} overflow-y-auto rounded-2xl border border-[#C89B3C]/30 bg-card p-5 shadow-2xl`}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="flex items-center gap-2 font-serif text-xl font-semibold">{Icon && <Icon className="h-5 w-5 text-[#C89B3C]" />} {title}</h2>
          <button type="button" onClick={() => closeRef.current()} aria-label="Close" data-testid={`${testid}-close`} className="rounded-full p-1.5 text-muted-foreground hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Find people by the start of their username (everyone with a username can be found) and ask to be their friend.
 *  Opens as a pop-up; `initialQuery` is whatever was typed in the search bar on the Friends page. */
export default function FindPeople({ initialQuery = "", onClose, onChanged }) {
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState(null);   // null = not searched yet
  const [busy, setBusy] = useState(false);
  const [exact, setExact] = useState("");


  useEffect(() => {
    if (initialQuery.trim().length < 3) return;
    api.get(`/people/search?q=${encodeURIComponent(initialQuery.trim())}`).then((r) => setResults(r.data))
      .catch((err) => toast.error(errText(err, "Search failed. Please try again.")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.get(`/people/search?q=${encodeURIComponent(q.trim())}`); setResults(r.data); }
    catch (err) { toast.error(errText(err, "Search failed. Please try again.")); }
    finally { setBusy(false); }
  };
  const sendExact = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/friends/request", { username: exact }); setExact(""); toast.success("Request sent (if they exist and are free to add)"); onChanged && onChanged(); }
    catch (err) { toast.error(errText(err, "Something went wrong. Please try again.")); }
    finally { setBusy(false); }
  };
  const setStatus = (username, status) => setResults((rs) => rs.map((r) => (r.username === username ? { ...r, status } : r)));
  const add = async (p) => {
    setBusy(true);
    try { await api.post("/friends/request", { username: p.username }); setStatus(p.username, "pending_out"); toast.success(`Request sent to ${p.username}`); onChanged && onChanged(); }
    catch (err) { toast.error(errText(err, "Could not send that. Please try again.")); }
    finally { setBusy(false); }
  };
  const accept = async (p) => {
    setBusy(true);
    try { await api.post(`/friends/${p.friendship_id}/respond`, { accept: true }); setStatus(p.username, "friends"); toast.success(`You and ${p.username} are now friends`); onChanged && onChanged(); }
    catch (err) { toast.error(errText(err, "Could not accept that. Please try again.")); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Find people" icon={Search} onClose={onClose} testid="find-people">
      <p className="mt-1 text-sm text-muted-foreground">Look up other theatre fans by the start of their username. Everyone with a username can be found, and they still decide whether to accept you.</p>

      <form onSubmit={search} className="mt-4 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value.toLowerCase())} maxLength={20} placeholder="Start of a username (3+ letters)" aria-label="Search usernames"
          data-testid="people-q" className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
        <button type="submit" disabled={busy || q.trim().length < 3} data-testid="people-search" className="rounded-full bg-[#C89B3C] px-4 py-2 text-sm font-semibold text-black disabled:opacity-60">Search</button>
      </form>

      {results && (
        results.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="people-none">No one found. Check the spelling, or they may not have chosen a username yet. You can also use their exact username below, or send them your invite link.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border" data-testid="people-results">
            {results.map((p) => (
              <li key={p.username} className="flex items-center justify-between gap-3 py-3" data-testid="person-row">
                <div className="min-w-0">
                  <p className="font-medium">{p.username}</p>
                </div>
                <div className="flex-shrink-0 text-sm">
                  {p.status === "friends" && <span className="inline-flex items-center gap-1 text-[hsl(var(--gold))]" data-testid="person-friends"><Check className="h-4 w-4" /> Friends</span>}
                  {p.status === "pending_out" && <span className="text-[hsl(var(--gold))]" data-testid="person-pending">Request sent ✓</span>}
                  {p.status === "pending_in" && <button type="button" disabled={busy} onClick={() => accept(p)} data-testid="person-accept" className="rounded-full bg-[#C89B3C] px-4 py-1.5 font-semibold text-black">Accept their request</button>}
                  {p.status === "none" && <button type="button" disabled={busy} onClick={() => add(p)} data-testid="person-add" className="rounded-full bg-[#C89B3C] px-4 py-1.5 font-semibold text-black">Add as friend</button>}
                </div>
              </li>
            ))}
          </ul>
        )
      )}

      <form onSubmit={sendExact} className="mt-5 border-t border-border pt-4">
        <label htmlFor="exact-username" className="text-sm font-medium">Already know their username?</label>
        <div className="mt-2 flex gap-2">
          <input id="exact-username" value={exact} onChange={(e) => setExact(e.target.value.toLowerCase())} maxLength={20} placeholder="their full username"
            data-testid="friend-username" className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          <button type="submit" disabled={busy || exact.trim().length < 3} data-testid="send-request"
            className="rounded-full bg-[#C89B3C] px-4 py-2 text-sm font-semibold text-black disabled:opacity-60">Add</button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Type their full username to send a request straight away.</p>
      </form>
    </Modal>
  );
}